//src/pages/inventory/Product/AddMedicineInventory.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  IconButton,
  Divider,
  InputAdornment,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  FormControl,
  FormHelperText
} from "@mui/material";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import dayjs from "dayjs";
import BasicModal from "../../../components/BasicModal";
import {
  NO_INNER_SCROLL_FORM_MODAL_SX,
  COMPACT_FORM_MODAL_SX,
  DENSE_FORM_BODY_SX,
  FORM_MODAL_CONTENT_SX,
} from "../../../utils/formModalStyles";
import FormModalActions from "../../../components/FormModalActions";
import {
  InvoiceSummarySection,
  calcManualSgstCgstTotal,
  resolveAttachmentUrl,
} from "../../../components/InvoiceFormShared";
import { createMedicineInventory, updateMedicineInventory, getExistingBatchNumbers } from "../../../services/medicineInventoryService";
import { createInvoice } from "../../../services/invoiceService";
import { uploadAttachment } from "../../../services/uploadService";
import {
  DROPS_PER_ML,
  isBoxOrSyrupMedicine,
  isFluidMedicine,
  usesDetailsMultiplier,
} from "../../../utils/medicinePricingUtils";
import { useSelector } from "react-redux";
import axios from "axios";
import { getOrganizationName, fetchAndStoreOrganizationName, getOrganizationId } from "../../../utils/organizationUtils";
import { createSupplier, searchSuppliers, getAllSuppliers } from "../../../services/supplierService";
import { getActiveSubscription } from "../../../services/subscriptionHistoryService";
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Link } from '@mui/material';
import StockAddInvoiceSupplierSection, {
  ADD_NEW_SUPPLIER_VALUE,
} from "../../../components/StockAddInvoiceSupplierSection";

const validationSchema = Yup.object({
  invoiceNumber: Yup.string().required("Invoice Number is required"),
  // totalCost is calculated dynamically, so we'll validate it manually in onSubmit
  totalCost: Yup.number().nullable().notRequired(),
  invoiceDate: Yup.date()
    .nullable()
    .typeError("Invoice Date is required")
    .required("Invoice Date is required"),
  invoiceType: Yup.string().required("Invoice Type is required"),
  supplierName: Yup.string().required("Supplier Name is required"),
  supplierContact: Yup.string().nullable(),
  supplierAddress: Yup.string().nullable(),
  supplierGstNumber: Yup.string().nullable(),
  attachment: Yup.string()
    .nullable()
    .required("Attachment is required")
    .trim()
    .min(1, "Attachment is required"),
  totalDiscount: Yup.mixed().nullable().notRequired(),
  sgst: Yup.mixed().nullable().notRequired(),
  cgst: Yup.mixed().nullable().notRequired(),
});

/** Allow decimal amounts like 259.82 while typing (e.g. "259."). */
const isDecimalAmountInput = (raw) =>
  raw === "" || /^\d*\.?\d*$/.test(String(raw));

const parseAmountValue = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const parsed = Number.parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const roundMoney2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** System price shown/stored is line total; override wins when user edits. */
const resolveMedicineSystemLinePrice = (entry) => {
  if (
    entry?.systemPriceOverride !== "" &&
    entry?.systemPriceOverride !== null &&
    entry?.systemPriceOverride !== undefined
  ) {
    return parseAmountValue(entry.systemPriceOverride, 0);
  }
  const unit = parseAmountValue(entry?.price, 0);
  const qty = parseAmountValue(entry?.quantity, 0);
  return roundMoney2(unit * qty);
};

const medicineEntryValidationSchema = Yup.object().shape({
  name: Yup.string().required("Medicine name is required"),
  batchNumber: Yup.string().required("Batch number is required"),
  quantity: Yup.number()
    .required("Quantity is required")
    .min(1, "Quantity must be at least 1"),
  price: Yup.number()
    .required("Price is required")
    .moreThan(0, "Price must be greater than 0"),
  vendorPrice: Yup.number()
    .required("Supplier price is required")
    .moreThan(0, "Supplier price must be greater than 0"),
  expiryDate: Yup.mixed()
    .nullable()
    .test("is-valid-date", "Expiry date is required", function(value) {
      if (value === null || value === undefined || value === '') {
        return this.createError({ message: "Expiry date is required" });
      }
      return true;
    }),
  details: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      // For box/syrup - return 1 if empty or null
      if (originalValue === "" || originalValue === null || originalValue === undefined) {
        return 1;
      }
      return value;
    })
    .min(1, "Details must be at least 1"),
});

const AddMedicineInventory = ({ open, onClose, product_info, refreshData }) => {
  const [medicine_list, setMedicine] = useState([]);
  const [batchNumberError, setBatchNumberError] = useState("");
  const [existingBatchNumbers, setExistingBatchNumbers] = useState([]);
  const [showBatchNumbersModal, setShowBatchNumbersModal] = useState(false);
  const [selectedEntryIndex, setSelectedEntryIndex] = useState(null); // State to track the selected entry index

  const [medicineEntries, setMedicineEntries] = useState([{
    name: "",
    batchNumber: "",
    quantity: '',
    price: '',
    systemPriceOverride: null, // null = auto (unit × qty); set when user edits System Price
    vendorPrice: '', // Initialize vendor price
    discount: '', // Initialize discount
    expiryDate: null,
    details: '',
    medicineDetails: '',
    manufacturer: "",
    medicineType: "", // Add medicine type
    medicineId: null, // Add medicine ID
    existingBatchNumbers: [] // Add this property to store batch numbers for each entry
  }]);
  
  // State to track touched fields for medicine entries
  const [touchedMedicineEntries, setTouchedMedicineEntries] = useState([{
    expiryDate: false
  }]);
  
  // State to track validation errors for medicine entries
  const [medicineEntryErrors, setMedicineEntryErrors] = useState([]);
  
  // Force re-render trigger for errors
  const [errorUpdateTrigger, setErrorUpdateTrigger] = useState(0);
  const [suppliersList, setSuppliersList] = useState([]);
  const [selectedSupplierKey, setSelectedSupplierKey] = useState("");
  const [isNewSupplier, setIsNewSupplier] = useState(false);

  const user = useSelector((state) => state.user.user);
  const [organizationName, setOrganizationName] = useState("Aryan Hospital"); // State to hold organization name, default to "Aryan Hospital"
  const [gstPercentage, setGstPercentage] = useState(null); // State to hold GST percentage from subscription
  const [attachment, setAttachment] = useState(null);

  const formik = useFormik({
  initialValues: {
    invoiceNumber: "",
    totalCost: 0, // Initialize with 0 instead of empty string to avoid validation issues
    invoiceDate: null,
    attachment: "",
    invoiceType: "",
    supplierName: "",
    supplierAddress: "",
    supplierContact: "",
    supplierGstNumber: "",
    totalDiscount: '', // Add total discount field
    sgst: '',
    cgst: '',
    payment_mode: { mode: "cash" },
    particulars: "",
  },
  validationSchema: validationSchema,
  validateOnChange: false, // Disable auto-validation on change to prevent totalCost errors
  validateOnBlur: false, // Disable auto-validation on blur to prevent totalCost errors
  onSubmit: async (values, { setTouched: setFormikTouched }) => {
    try {
      // Step 0: Calculate and set totalCost before validation
      const totalSystemPriceForCalc = medicineEntries.reduce((sum, entry) => {
        return sum + resolveMedicineSystemLinePrice(entry);
      }, 0);
      
      const totalDiscountForCalc = parseFloat(values.totalDiscount) || 0;
      const amountAfterDiscountForCalc = totalSystemPriceForCalc - totalDiscountForCalc;
      const sgstForCalc = parseFloat(values.sgst) || 0;
      const cgstForCalc = parseFloat(values.cgst) || 0;
      const calculatedTotalCost =
        amountAfterDiscountForCalc + sgstForCalc + cgstForCalc;
      
      // Set totalCost in formik so validation passes (use false to not trigger validation immediately)
      await formik.setFieldValue('totalCost', calculatedTotalCost.toFixed(2), false);
      
      // Store calculatedTotalCost for later use in validation
      const calculatedTotalCostForValidation = calculatedTotalCost;
      
      // Step 1: Mark all form fields as touched to show errors
      setFormikTouched({
        invoiceNumber: true,
        invoiceDate: true,
        invoiceType: true,
        totalCost: true,
        supplierName: true,
        supplierContact: true,
        supplierAddress: true,
        supplierGstNumber: true,
        attachment: true,
      }, false);
      
      // Step 2: Mark all expiry dates as touched
      const newTouched = medicineEntries.map(() => ({ expiryDate: true }));
      setTouchedMedicineEntries(newTouched);
      
      // Step 3: Wait a bit for state to update
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Step 4: Clear invoice date error if date exists
      // Check if invoiceDate is not null/undefined/empty string
      const hasInvoiceDateInitial = values.invoiceDate !== null && 
                                    values.invoiceDate !== undefined && 
                                    values.invoiceDate !== '';
      
      if (hasInvoiceDateInitial) {
        formik.setFieldError("invoiceDate", undefined);
      } else {
        formik.setFieldError("invoiceDate", "Invoice Date is required");
        setErrorUpdateTrigger(prev => prev + 1);
      }
      
      // Step 5: Validate formik form explicitly (but skip invoiceDate and totalCost - we handle them manually)
      let formErrors = {};
      try {
        await formik.validateForm();
        formErrors = { ...formik.errors };
      } catch (error) {
        formErrors = { ...formik.errors };
      }
      
      // Remove invoiceDate and totalCost from validation errors - we handle them manually
      delete formErrors.invoiceDate;
      delete formErrors.totalCost;
      
      // Step 6: Check for expiry date errors
      const expiryDateErrors = [];
      medicineEntries.forEach((entry, idx) => {
        if (!entry.expiryDate) {
          expiryDateErrors.push(idx);
        }
      });
      
      // Step 7: Set expiry date errors if any
      if (expiryDateErrors.length > 0) {
        const updatedErrors = [...medicineEntryErrors];
        expiryDateErrors.forEach(idx => {
          if (!updatedErrors[idx]) {
            updatedErrors[idx] = {};
          }
          updatedErrors[idx].expiryDate = "Expiry date is required";
        });
        setMedicineEntryErrors(updatedErrors);
        setErrorUpdateTrigger(prev => prev + 1);
      }
      
      // Step 8: Final validation check - use direct value checks
      const finalFormErrors = {};
      let hasErrors = false;
      
      // Check invoice number
      if (!values.invoiceNumber || String(values.invoiceNumber).trim() === '') {
        finalFormErrors.invoiceNumber = "Invoice Number is required";
        hasErrors = true;
      }
      
      // Check invoice date - use current value
      const invoiceDateToCheck = formik.values.invoiceDate || values.invoiceDate;
      const hasInvoiceDateFinal = invoiceDateToCheck !== null && 
                                 invoiceDateToCheck !== undefined && 
                                 invoiceDateToCheck !== '';
      
      console.log("🔍 Validation check - invoiceDate:", {
        values_invoiceDate: values.invoiceDate,
        formik_invoiceDate: formik.values.invoiceDate,
        invoiceDateToCheck: invoiceDateToCheck,
        hasInvoiceDateFinal: hasInvoiceDateFinal
      });
      
      if (!hasInvoiceDateFinal) {
        finalFormErrors.invoiceDate = "Invoice Date is required";
        hasErrors = true;
      } else {
        // Date exists - ensure error is cleared
        formik.setFieldError("invoiceDate", undefined);
      }
      
      // Check invoice type
      if (!values.invoiceType || String(values.invoiceType).trim() === '') {
        finalFormErrors.invoiceType = "Invoice Type is required";
        hasErrors = true;
      }
      
      // Check total cost - use the calculated value we set earlier
      // The totalCost should already be set in formik, but validate it anyway
      const totalCostValue = parseFloat(formik.values.totalCost) || calculatedTotalCostForValidation;
      
      console.log("💰 Total cost validation:", {
        formikTotalCost: formik.values.totalCost,
        calculatedTotalCost: calculatedTotalCostForValidation,
        totalCostValue: totalCostValue,
        isNaN: isNaN(totalCostValue),
        isGreaterThanZero: totalCostValue > 0
      });
      
      if (!totalCostValue || totalCostValue <= 0 || isNaN(totalCostValue)) {
        finalFormErrors.totalCost = "Total Cost must be greater than 0. Please add medicines.";
        hasErrors = true;
      } else {
        // Clear any existing totalCost error since we have a valid value
        if (finalFormErrors.totalCost) {
          delete finalFormErrors.totalCost;
        }
        formik.setFieldError('totalCost', undefined);
      }
      
      // Attachment is mandatory for add stock (must be a selected file)
      if (!(attachment instanceof File)) {
        finalFormErrors.attachment = "Attachment is required";
        hasErrors = true;
      }

      // Check supplier name
      if (!selectedSupplierKey) {
        finalFormErrors.supplierName = "Please select a supplier or add a new one";
        hasErrors = true;
      } else if (!values.supplierName || String(values.supplierName).trim() === '') {
        finalFormErrors.supplierName = "Supplier Name is required";
        hasErrors = true;
      }
      
      // New supplier: require contact / address / GST; existing: details are read-only
      if (isNewSupplier || selectedSupplierKey === ADD_NEW_SUPPLIER_VALUE) {
        if (!values.supplierContact || String(values.supplierContact).trim() === '') {
          finalFormErrors.supplierContact = "Supplier Contact is required";
          hasErrors = true;
        } else {
          const digitsOnly = String(values.supplierContact).replace(/\D/g, '');
          if (digitsOnly.length !== 10) {
            finalFormErrors.supplierContact = "Supplier Contact must be exactly 10 digits";
            hasErrors = true;
          }
        }
        
        if (!values.supplierAddress || String(values.supplierAddress).trim() === '') {
          finalFormErrors.supplierAddress = "Supplier Address is required";
          hasErrors = true;
        }
        
        if (!values.supplierGstNumber || String(values.supplierGstNumber).trim() === '') {
          finalFormErrors.supplierGstNumber = "GST Number is required";
          hasErrors = true;
        }
      }
      
      // Step 10: Check for medicine entry errors - validate each entry
      const medicineValidationErrors = [];
      medicineEntries.forEach((entry, idx) => {
        const entryErrors = {};
        
        if (!entry.name || String(entry.name).trim() === '') {
          entryErrors.name = "Medicine name is required";
          hasErrors = true;
        }
        
        if (!entry.batchNumber || String(entry.batchNumber).trim() === '') {
          entryErrors.batchNumber = "Batch number is required";
          hasErrors = true;
        }
        
        if (!entry.expiryDate) {
          entryErrors.expiryDate = "Expiry date is required";
          hasErrors = true;
        }
        
        if (!entry.quantity || parseFloat(entry.quantity) <= 0) {
          entryErrors.quantity = "Quantity is required and must be greater than 0";
          hasErrors = true;
        }
        
        if (!(resolveMedicineSystemLinePrice(entry) > 0)) {
          entryErrors.price = "System price is required and must be greater than 0";
          hasErrors = true;
        }
        
        if (entry.vendorPrice === '' || entry.vendorPrice === null || entry.vendorPrice === undefined || !(parseAmountValue(entry.vendorPrice) > 0)) {
          entryErrors.vendorPrice = "Supplier price is required and must be greater than 0";
          hasErrors = true;
        }
        
        if (Object.keys(entryErrors).length > 0) {
          medicineValidationErrors[idx] = entryErrors;
        }
      });
      
      // Step 11: If there are errors, stop submission and show them
      if (hasErrors) {
        // Set form errors
        Object.keys(finalFormErrors).forEach(key => {
          formik.setFieldError(key, finalFormErrors[key]);
        });
        
        // Set medicine entry errors
        if (medicineValidationErrors.length > 0) {
          setMedicineEntryErrors(medicineValidationErrors);
        }
        
        // Force re-render to show errors
        setErrorUpdateTrigger(prev => prev + 1);
        
        // Debug: Log errors
        console.log("❌ Form validation failed!");
        console.log("Form errors:", finalFormErrors);
        console.log("Medicine errors:", medicineValidationErrors);
        console.log("Invoice date value:", values.invoiceDate);
        console.log("Invoice date type:", typeof values.invoiceDate);
        console.log("Invoice date is null?", values.invoiceDate === null);
        console.log("Invoice date is undefined?", values.invoiceDate === undefined);
        console.log("Invoice date toString:", values.invoiceDate?.toString());
        console.log("Medicine entries:", medicineEntries);
        console.log("Total discount:", values.totalDiscount);
        
        // Show alert with validation errors
        const errorMessages = [];
        Object.keys(finalFormErrors).forEach(key => {
          errorMessages.push(`${key}: ${finalFormErrors[key]}`);
        });
        if (medicineValidationErrors.length > 0) {
          medicineValidationErrors.forEach((err, idx) => {
            if (err && Object.keys(err).length > 0) {
              Object.keys(err).forEach(field => {
                errorMessages.push(`Medicine ${idx + 1} - ${field}: ${err[field]}`);
              });
            }
          });
        }
        alert(`Please fix the following errors:\n\n${errorMessages.join('\n')}`);
        
        return;
      }
      
      // All validations passed - proceed with submission
      console.log("✅ All validations passed, proceeding with submission");
      console.log("📋 Submission details:", {
        invoiceDate: values.invoiceDate,
        invoiceDateType: typeof values.invoiceDate,
        medicineEntriesCount: medicineEntries.length,
        totalDiscount: values.totalDiscount,
        invoiceNumber: values.invoiceNumber,
        invoiceType: values.invoiceType,
        supplierName: values.supplierName
      });

    // Calculate totals from supplier (vendor) amount — do not multiply by qty.
    const totalSupplierPrice = medicineEntries.reduce((sum, entry) => {
      const supplierAmount = parseFloat(entry.vendorPrice) || 0;
      return sum + supplierAmount;
    }, 0);

    // Calculate base amount (before tax) - supplier total minus discount
    const totalDiscount = parseFloat(values.totalDiscount) || 0;
    
    const baseAmount = totalSupplierPrice - totalDiscount;

    // Use manually entered SGST / CGST amounts (not fixed GST %)
    const amountAfterDiscount = totalSupplierPrice - totalDiscount;
    const sgst = Math.round((parseFloat(values.sgst) || 0) * 100) / 100;
    const cgst = Math.round((parseFloat(values.cgst) || 0) * 100) / 100;
    const totalAmount = amountAfterDiscount + sgst + cgst;

    const amount = baseAmount;

      const req_data = {
        ...values,
        userId: user.id,
        amount: amount, // Set the amount
        sgst: sgst, // Set SGST
        cgst: cgst, // Set CGST
        totalAmount: totalAmount, // Set total amount
        medicineEntries: medicineEntries.map(entry => {
          // Handle details field properly for different medicine types
          const details = entry.details === "" || entry.details === null || entry.details === undefined ? 1 : entry.details;
          
          // Ensure expiryDate is valid before formatting
          let formattedExpiryDate = null;
          if (entry.expiryDate) {
            try {
              formattedExpiryDate = dayjs(entry.expiryDate).format("YYYY-MM-DD");
            } catch (error) {
              console.error("Error formatting expiry date:", error);
              throw new Error(`Invalid expiry date for medicine: ${entry.name}`);
            }
          }
          
          return {
            ...entry,
            expiryDate: formattedExpiryDate,
            details: details, // Set the processed details value
            // Calculate final quantity and price based on details
            finalQuantity: details === 0 ? entry.quantity : entry.quantity * details,
            // System line total (supports decimals e.g. 259.82); user override if edited
            finalPrice: roundMoney2(resolveMedicineSystemLinePrice(entry)),
            vendorPrice: parseAmountValue(entry.vendorPrice, 0), // Include vendor price
            manufacturer: entry.manufacturer, // Include manufacturer
            medicineType: entry.medicineType, // Include medicine type
            medicineId: entry.medicineId, // Include medicine ID
          };
        }),
      };

      // Create medicine inventory records
      const medicineInventoryPromises = req_data.medicineEntries.map(async (entry) => {
        // Validate that medicineId exists before creating inventory
        if (!entry.medicineId) {
          throw new Error(`Medicine ID is missing for medicine: ${entry.name}. Please select a valid medicine from the list.`);
        }
        
        console.log(`[AddMedicineInventory] Creating inventory for medicine ID: ${entry.medicineId}, name: ${entry.name}`);
        
        const inventoryData = {
          name: entry.name,
          batchNumber: entry.batchNumber,
          quantity: entry.finalQuantity, // Use final quantity
          price: entry.finalPrice, // Use final price (supports decimals e.g. 259.82)
          vendorPrice: parseAmountValue(entry.vendorPrice, 0), // Include vendor price
          expiryDate: entry.expiryDate,
          manufacturer: entry.manufacturer, // Include manufacturer
          medicineType: entry.medicineType, // Include medicine type
          medicineId: entry.medicineId, // Include medicine ID (must exist in organization-specific table)
          userId: user.id,
        };
        
        try {
          console.log(`[AddMedicineInventory] Creating inventory with data:`, inventoryData);
          const createdInventory = await createMedicineInventory(inventoryData);
          console.log(`[AddMedicineInventory] ✅ Successfully created inventory for medicine ID: ${entry.medicineId}`, createdInventory);
          
          if (!createdInventory || !createdInventory.id) {
            throw new Error(`Inventory creation failed for "${entry.name}": No inventory ID returned from server`);
          }
          
          return createdInventory; // Return the created inventory object
        } catch (error) {
          console.error(`[AddMedicineInventory] ❌ Error creating inventory for medicine ID: ${entry.medicineId}`, error);
          console.error(`[AddMedicineInventory] Error response:`, error.response?.data);
          console.error(`[AddMedicineInventory] Error status:`, error.response?.status);
          
          // Provide more helpful error message
          if (error.response?.status === 500 && 
              error.response?.data?.message?.includes("foreign key constraint")) {
            throw new Error(`Failed to create inventory for "${entry.name}": The medicine (ID: ${entry.medicineId}) does not exist in the organization-specific medicine table. Please ensure the medicine was created in the correct organization table.`);
          }
          
          const errorMessage = error.response?.data?.message || error.message || "Unknown error";
          throw new Error(`Failed to create inventory for "${entry.name}": ${errorMessage}`);
        }
      });

      // Wait for all medicine inventory records to be created
    const medicineInventories = await Promise.all(medicineInventoryPromises);

      // Step: Handle supplier data - use selected existing supplier or create new
      let supplierId = null;
      if (
        selectedSupplierKey &&
        selectedSupplierKey !== ADD_NEW_SUPPLIER_VALUE &&
        !isNewSupplier
      ) {
        supplierId = Number(selectedSupplierKey) || selectedSupplierKey;
        console.log(`[AddMedicineInventory] ✅ Using selected supplier ID: ${supplierId}`);
      } else if (values.supplierName && values.supplierName.trim()) {
        try {
          const organizationId = getOrganizationId(user);
          const supplierNameTrimmed = values.supplierName.trim();
          
          // First, search for existing supplier by name
          let existingSuppliers = [];
          try {
            existingSuppliers = await searchSuppliers(supplierNameTrimmed);
          } catch (searchError) {
            console.warn(`[AddMedicineInventory] Error searching suppliers, will try to create:`, searchError);
          }
          
          // Check if supplier exists (case-insensitive match)
          const matchingSupplier = existingSuppliers.find(
            (s) => (s.name || '').toLowerCase().trim() === supplierNameTrimmed.toLowerCase().trim()
          );
          
          if (matchingSupplier) {
            supplierId = matchingSupplier.id;
            console.log(`[AddMedicineInventory] ✅ Found existing supplier: ${supplierNameTrimmed} (ID: ${supplierId})`);
          } else {
            // Create new supplier
            const supplierData = {
              name: supplierNameTrimmed,
              address: values.supplierAddress?.trim() || null,
              contact: values.supplierContact?.trim() || null,
              gstNumber: values.supplierGstNumber?.trim() || null,
            };
            
            // Add organizationId if available (backend will handle organization-specific table)
            if (organizationId && organizationId > 0) {
              supplierData.organizationId = organizationId;
            }
            
            console.log(`[AddMedicineInventory] Creating new supplier: ${supplierNameTrimmed}...`);
            console.log(`[AddMedicineInventory] Supplier data:`, supplierData);
            
            try {
              const createdSupplier = await createSupplier(supplierData);
              console.log(`[AddMedicineInventory] Supplier creation response:`, createdSupplier);
              
              // Handle different response structures
              const supplierIdFromResponse = createdSupplier?.id || createdSupplier?.data?.id || null;
              
              if (supplierIdFromResponse) {
                supplierId = supplierIdFromResponse;
                console.log(`[AddMedicineInventory] ✅ Created new supplier: ${supplierNameTrimmed} (ID: ${supplierId})`);
              } else {
                console.error(`[AddMedicineInventory] ⚠️ Supplier created but no ID found in response:`, createdSupplier);
                throw new Error("Supplier created but no ID returned");
              }
            } catch (createError) {
              console.error(`[AddMedicineInventory] Error creating supplier:`, createError);
              
              // If creation failed due to transaction error, try searching again
              // (supplier might have been created by another transaction)
              if (createError.message && (
                createError.message.includes("transaction") || 
                createError.message.includes("updated or deleted") ||
                createError.message.includes("unsaved-value")
              )) {
                console.log(`[AddMedicineInventory] Transaction error detected, searching for supplier again...`);
                try {
                  // Wait a bit before retrying search
                  await new Promise(resolve => setTimeout(resolve, 500));
                  const retrySuppliers = await searchSuppliers(supplierNameTrimmed);
                  const retryMatchingSupplier = retrySuppliers.find(
                    (s) => (s.name || '').toLowerCase().trim() === supplierNameTrimmed.toLowerCase().trim()
                  );
                  
                  if (retryMatchingSupplier && retryMatchingSupplier.id) {
                    supplierId = retryMatchingSupplier.id;
                    console.log(`[AddMedicineInventory] ✅ Found supplier after retry: ${supplierNameTrimmed} (ID: ${supplierId})`);
                  } else {
                    throw new Error("Supplier not found after retry");
                  }
                } catch (retryError) {
                  console.error(`[AddMedicineInventory] Retry search also failed:`, retryError);
                  throw createError; // Throw original error
                }
              } else {
                throw createError; // Re-throw if it's not a transaction error
              }
            }
          }
        } catch (supplierError) {
          console.error(`[AddMedicineInventory] Error handling supplier:`, supplierError);
          console.error(`[AddMedicineInventory] Supplier error details:`, {
            message: supplierError.message,
            response: supplierError.response?.data,
            status: supplierError.response?.status
          });
          
          // If we still don't have a supplier ID, try one more search as last resort
          if (!supplierId && values.supplierName && values.supplierName.trim()) {
            try {
              console.log(`[AddMedicineInventory] Last resort: searching for supplier one more time...`);
              const lastResortSuppliers = await searchSuppliers(values.supplierName.trim());
              const lastResortMatch = lastResortSuppliers.find(
                (s) => (s.name || '').toLowerCase().trim() === values.supplierName.trim().toLowerCase()
              );
              if (lastResortMatch && lastResortMatch.id) {
                supplierId = lastResortMatch.id;
                console.log(`[AddMedicineInventory] ✅ Found supplier on last resort search: ${values.supplierName} (ID: ${supplierId})`);
              }
            } catch (lastResortError) {
              console.error(`[AddMedicineInventory] Last resort search failed:`, lastResortError);
            }
          }
          
          // If we still don't have supplierId, log warning but continue
          if (!supplierId) {
            console.warn(`[AddMedicineInventory] ⚠️ Could not get supplier ID, continuing without it. Invoice will be created without supplierId.`);
          }
        }
      }
      
      // Log final supplier ID status
      console.log(`[AddMedicineInventory] 📋 Final supplier ID before invoice creation:`, {
        supplierId: supplierId,
        supplierIdType: typeof supplierId,
        supplierIdValue: supplierId,
        isNull: supplierId === null,
        isUndefined: supplierId === undefined
      });
      
      if (supplierId) {
        console.log(`[AddMedicineInventory] ✅ Supplier ID will be included in invoice: ${supplierId}`);
      } else {
        console.warn(`[AddMedicineInventory] ⚠️ No supplier ID available for invoice`);
      }

      // Create an invoice
      let formattedInvoiceDate = null;
      if (values.invoiceDate) {
        try {
          formattedInvoiceDate = dayjs(values.invoiceDate).format("YYYY-MM-DD");
        } catch (error) {
          console.error("Error formatting invoice date:", error);
          throw new Error("Invalid invoice date");
        }
      }

      let attachmentUrl = null;
      if (attachment instanceof File) {
        const uploaded = await uploadAttachment(attachment);
        attachmentUrl = resolveAttachmentUrl(uploaded);
      }
      
      const invoiceData = {
        invoiceNumber: values.invoiceNumber,
        amount: amount, // Base amount (total system price minus discount, before tax)
        discount: totalDiscount, // Total discount - stored in invoice table
        sgst: sgst, // Include SGST in the invoice data
        cgst: cgst, // Include CGST in the invoice data
        totalAmount: totalAmount, // Use the calculated total amount
        date: formattedInvoiceDate,
        invoiceType: values.invoiceType,
        fromName: values.supplierName?.trim() || organizationName,
        userId: user?.id || null, // Logged-in user who created this invoice (for tracking)
        supplierId: supplierId || null, // Supplier ID - stored in organization-specific invoice table (ensure it's not undefined)
        // Supplier details for invoice
        supplierName: values.supplierName,
        supplierAddress: values.supplierAddress,
        supplierContact: values.supplierContact,
        supplierGstNumber: values.supplierGstNumber,
        paymentMode: (values.payment_mode?.mode || "cash").toUpperCase(),
        creditOrganizationId:
          values.payment_mode?.mode === "credit"
            ? values.payment_mode?.organizationId || null
            : null,
        particulars: values.particulars?.trim?.() || "",
        ...(attachmentUrl ? { attachment: attachmentUrl } : {}),
      };
      
      // Log invoice data to verify supplierId is included
      console.log(`[AddMedicineInventory] 📝 Invoice data before sending:`, {
        ...invoiceData,
        supplierId: invoiceData.supplierId,
        supplierIdType: typeof invoiceData.supplierId,
        supplierIdValue: invoiceData.supplierId
      });
      let createdInvoice;
      try {
        createdInvoice = await createInvoice(invoiceData); // Create the invoice
        console.log("✅ Invoice created successfully:", createdInvoice);
        
        if (!createdInvoice || !createdInvoice.id) {
          throw new Error("Invoice creation failed: No invoice ID returned from server");
        }
      } catch (invoiceError) {
        console.error("❌ Error creating invoice:", invoiceError);
        console.error("Invoice error response:", invoiceError.response?.data);
        console.error("Invoice error status:", invoiceError.response?.status);
        const invoiceErrorMessage = invoiceError.response?.data?.message || invoiceError.message || "Failed to create invoice";
        throw new Error(`Failed to create invoice: ${invoiceErrorMessage}`);
      }

      // Update medicine inventory records with the invoice ID
      console.log("🔗 Linking inventory records to invoice ID:", createdInvoice.id);
      const updatePromises = medicineInventories.map(async (inventory) => {
        try {
          const updatedInventoryData = {
            ...inventory,
            invoiceId: createdInvoice.id, // Link the invoice ID
          };
          console.log(`[AddMedicineInventory] Updating inventory ${inventory.id} with invoice ID ${createdInvoice.id}`);
          await updateMedicineInventory(inventory.id, updatedInventoryData); // Update the inventory record
          console.log(`[AddMedicineInventory] ✅ Successfully linked inventory ${inventory.id} to invoice`);
        } catch (updateError) {
          console.error(`[AddMedicineInventory] ❌ Error updating inventory ${inventory.id}:`, updateError);
          throw new Error(`Failed to link inventory record to invoice: ${updateError.message || "Unknown error"}`);
        }
      });
      // Wait for all inventory records to be updated
      await Promise.all(updatePromises);
      console.log("✅ All inventory records linked to invoice successfully");
      
      // Refresh data if callback is provided
      if (refreshData) {
        refreshData();
      }
      
      handelCloseModalState(); // Close the modal
    } catch (error) {
      console.error("❌ Error submitting form:", error);
      console.error("❌ Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      const errorMessage = error.response?.data?.message || error.message || "Please check all fields and try again";
      alert(`Error creating stock: ${errorMessage}`);
      // Don't close modal on error so user can fix issues
    }
  },
});

  const fetchMedicine = async () => {
    try {
      console.log(`[AddMedicineInventory] Fetching medicines from basic medicine table (not organization-specific)...`);
      
      // Get backend URL - use same logic as apiClient
      const getEnvVar = (key, defaultValue = null) => {
        if (typeof window !== 'undefined' && window.env && typeof window.env === 'object' && window.env[key]) {
          return window.env[key];
        }
        if (process.env && process.env[key]) {
          return process.env[key];
        }
        return defaultValue;
      };
      
      const BACKEND_URL = getEnvVar('REACT_APP_BACKEND_URL', 'http://localhost:8080/api');
      const apiUrl = `${BACKEND_URL}/medicines`;
      
      // Get authentication token
      const token = localStorage.getItem("token");
      
      // Use axios directly to fetch from base table (without organizationId)
      // This ensures we fetch from the basic medicine table, not organization-specific table
      const response = await axios.get(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      
      const data = response.data;
      console.log(`[AddMedicineInventory] ✅ Fetched ${Array.isArray(data) ? data.length : 0} medicines from basic table`);
      if (Array.isArray(data) && data.length > 0) {
        console.log(`[AddMedicineInventory] Sample medicine:`, { id: data[0].id, name: data[0].medicineName });
      }
      setMedicine(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(`[AddMedicineInventory] ❌ Error fetching medicines:`, error);
      alert(`Failed to fetch medicines: ${error.message || "Unknown error"}`);
      setMedicine([]); // Set empty array on error
    }
  };

  // Generate next batch number in the medicineName_batchNumber format
  const generateNextBatchNumber = (medicineName, existingBatchNumbers = []) => {
    if (!medicineName || medicineName.trim() === '') {
      return "MD001"; // Fallback if no medicine name
    }
    
    // Clean medicine name: remove special characters, replace spaces with underscores
    const cleanMedicineName = medicineName.trim().replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_');
    
    // Filter batch numbers that match the medicine name pattern (medicineName_###)
    const medicineBatchNumbers = existingBatchNumbers
      .filter(batch => {
        const batchStr = String(batch.batchNumber || batch);
        return batchStr.startsWith(`${cleanMedicineName}_`);
      })
      .map(batch => {
        const batchStr = String(batch.batchNumber || batch);
        // Extract numeric part after medicineName_
        const match = batchStr.match(new RegExp(`^${cleanMedicineName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_(\\d+)$`));
        return match ? parseInt(match[1], 10) : null;
      })
      .filter(num => num !== null)
      .sort((a, b) => b - a); // Sort descending
    
    // Get the highest batch number or start from 1
    const nextNumber = medicineBatchNumbers.length > 0 ? medicineBatchNumbers[0] + 1 : 1;
    
    return `${cleanMedicineName}_${nextNumber.toString().padStart(3, '0')}`;
  };

// Fetch existing batch numbers for selected medicine
const fetchExistingBatchNumbers = async (medicineId, index, medicineName = null) => {
  try {
    const data = await getExistingBatchNumbers(medicineId);
    // Assuming data is an array of objects with batchNumber and quantity
    const batchNumbersWithQuantities = data.map(item => ({
      batchNumber: item.batchNumber,
      quantity: item.quantity
    }));
    
    // Get the medicine name from parameter or current entry
    const currentEntry = medicineEntries[index];
    const finalMedicineName = medicineName || currentEntry?.name || '';
    
    // Update only the specific medicine entry's existingBatchNumbers
    setMedicineEntries(prevEntries => {
      const newEntries = [...prevEntries];
      newEntries[index].existingBatchNumbers = batchNumbersWithQuantities; // Update the specific entry
      return newEntries;
    });

    // Generate next batch number using medicine name format
    const nextBatchNumber = generateNextBatchNumber(finalMedicineName, batchNumbersWithQuantities);
    
    setMedicineEntries(prevEntries => {
      const newEntries = [...prevEntries];
      newEntries[index].batchNumber = nextBatchNumber; // Update the specific entry
      return newEntries;
    });
  } catch (error) {
    if (error.response?.status === 404) {
      // Get the medicine name from parameter or current entry
      const currentEntry = medicineEntries[index];
      const finalMedicineName = medicineName || currentEntry?.name || '';
      
      // Generate default batch number using medicine name format
      const defaultBatchNumber = generateNextBatchNumber(finalMedicineName, []);
      
      setMedicineEntries(prevEntries => {
        const newEntries = [...prevEntries];
        newEntries[index].batchNumber = defaultBatchNumber;
        newEntries[index].existingBatchNumbers = []; // Clear any existing batch numbers
        return newEntries;
      });
    } else {
      console.error("Error fetching batch numbers:", error);
    }
  }
};

  useEffect(() => {
    fetchMedicine();
    // fetchExistingBatchNumbers();
  }, []);

  // Add useEffect to refresh medicine list when modal opens
  useEffect(() => {
    if (open) {
      fetchMedicine();
      const loadSuppliers = async () => {
        try {
          const list = await getAllSuppliers();
          setSuppliersList(Array.isArray(list) ? list : []);
        } catch (error) {
          console.warn("[AddMedicineInventory] Could not load suppliers:", error);
          setSuppliersList([]);
        }
      };
      loadSuppliers();
    }
  }, [open]);

  const handleSupplierKeyChange = (key) => {
    setSelectedSupplierKey(key);
    formik.setFieldTouched("supplierName", true, false);
    if (!key) {
      setIsNewSupplier(false);
      formik.setFieldValue("supplierName", "");
      formik.setFieldValue("supplierContact", "");
      formik.setFieldValue("supplierGstNumber", "");
      formik.setFieldValue("supplierAddress", "");
      return;
    }
    if (key === ADD_NEW_SUPPLIER_VALUE) {
      setIsNewSupplier(true);
      formik.setFieldValue("supplierName", "");
      formik.setFieldValue("supplierContact", "");
      formik.setFieldValue("supplierGstNumber", "");
      formik.setFieldValue("supplierAddress", "");
      return;
    }
    setIsNewSupplier(false);
    const selected = suppliersList.find((s) => String(s.id) === String(key));
    if (selected) {
      formik.setFieldValue("supplierName", selected.name || selected.supplierName || "");
      formik.setFieldValue("supplierContact", selected.contact || "");
      formik.setFieldValue("supplierGstNumber", selected.gstNumber || "");
      formik.setFieldValue("supplierAddress", selected.address || "");
      formik.setFieldError("supplierName", undefined);
      formik.setFieldError("supplierContact", undefined);
      formik.setFieldError("supplierGstNumber", undefined);
      formik.setFieldError("supplierAddress", undefined);
    }
  };

  const medicinesForInvoiceType = (() => {
    const invoiceType = String(formik.values.invoiceType || "").trim().toLowerCase();
    if (invoiceType === "homeopathy" || invoiceType === "allopathy") {
      return medicine_list.filter(
        (m) =>
          String(m.medicineType || "").trim().toLowerCase() === invoiceType
      );
    }
    return medicine_list;
  })();

  // Clear medicine selections that no longer match invoice type
  useEffect(() => {
    const invoiceType = String(formik.values.invoiceType || "").trim().toLowerCase();
    if (invoiceType !== "homeopathy" && invoiceType !== "allopathy") return;
    const allowed = medicine_list.filter(
      (m) => String(m.medicineType || "").trim().toLowerCase() === invoiceType
    );
    setMedicineEntries((prev) =>
      prev.map((entry) => {
        if (!entry.name) return entry;
        const stillValid = allowed.some((m) => m.medicineName === entry.name);
        if (stillValid) return entry;
        return {
          ...entry,
          name: "",
          batchNumber: "",
          medicineId: null,
          manufacturer: "",
          medicineType: "",
          medicineDetails: "",
          details: "",
          price: "",
          existingBatchNumbers: [],
        };
      })
    );
  }, [formik.values.invoiceType, medicine_list]);

  // Fetch organization name and GST percentage when modal opens
  useEffect(() => {
    const fetchOrganizationNameAndGST = async () => {
      if (open) {
        try {
          // First try to get from user object or localStorage
          let orgName = getOrganizationName(user);
          const organizationId = getOrganizationId(user);
          
          // If not found, try to fetch it
          if (!orgName && organizationId && organizationId > 0) {
            orgName = await fetchAndStoreOrganizationName(organizationId);
          }
          
          // Set organization name, or keep default "Aryan Hospital" for basic users
          setOrganizationName(orgName || "Aryan Hospital");
          
          // Fetch active subscription to get GST percentage
          if (organizationId && organizationId > 0) {
            try {
              const activeSubscription = await getActiveSubscription(organizationId);
              if (activeSubscription && activeSubscription.gstPercentage) {
                setGstPercentage(parseFloat(activeSubscription.gstPercentage));
                console.log(`[AddMedicineInventory] Fetched GST percentage from subscription: ${activeSubscription.gstPercentage}%`);
              } else {
                // Default to 8.5% (4.25% CGST + 4.25% SGST) if no GST in subscription
                setGstPercentage(8.5);
                console.log(`[AddMedicineInventory] No GST in subscription, using default 8.5%`);
              }
            } catch (gstError) {
              console.warn(`[AddMedicineInventory] Error fetching GST from subscription:`, gstError);
              // Default to 8.5% on error
              setGstPercentage(8.5);
            }
          } else {
            // Default to 8.5% if no organization ID
            setGstPercentage(8.5);
          }
        } catch (error) {
          console.error(`[AddMedicineInventory] Error fetching organization name:`, error);
          // Keep default "Aryan Hospital" on error
          setOrganizationName("Aryan Hospital");
          setGstPercentage(8.5);
        }
      } else if (!open) {
        // Reset organization name and GST when modal closes
        setOrganizationName("Aryan Hospital");
        setGstPercentage(null);
      }
    };

    fetchOrganizationNameAndGST();
  }, [open, user]);

  const handelCloseModalState = () => {
    // window.location.reload();
    onClose();
    formik.resetForm();
    setMedicineEntries([{
      name: "",
      batchNumber: "",
      quantity: '',
      price: '',
      systemPriceOverride: null,
      vendorPrice: '', // Initialize vendor price
      discount: '', // Initialize discount
      expiryDate: null,
      details: '',
      medicineDetails: '',
      manufacturer: "",
      medicineType: "",
      medicineId: null,
      existingBatchNumbers: []
    }]);
    // Reset supplier fields
    formik.setFieldValue('supplierName', '');
    formik.setFieldValue('supplierAddress', '');
    formik.setFieldValue('supplierContact', '');
    formik.setFieldValue('supplierGstNumber', '');
    formik.setFieldValue('totalDiscount', '');
    formik.setFieldValue('sgst', '');
    formik.setFieldValue('cgst', '');
    setSelectedSupplierKey("");
    setIsNewSupplier(false);
    setBatchNumberError("");
    setSelectedEntryIndex(null); // Reset selected entry index
    // Reset touched and error states
    setTouchedMedicineEntries([{ expiryDate: false }]);
    setMedicineEntryErrors([]);
  };

  const addMedicineEntry = () => {
    setMedicineEntries([...medicineEntries, {
      name: "",
      batchNumber: "",
      quantity: 0,
      price: 0,
      systemPriceOverride: null,
      vendorPrice: 0, // Initialize vendor price
      discount: 0, // Initialize discount
      expiryDate: null,
      details: 0,
      medicineDetails: "",
      manufacturer: "",
      medicineType: "",
      medicineId: null,
      existingBatchNumbers: []
    }]);
    // Add touched state for new entry
    setTouchedMedicineEntries([...touchedMedicineEntries, { expiryDate: false }]);
    // Add error state for new entry
    setMedicineEntryErrors([...medicineEntryErrors, {}]);
  };

  const removeMedicineEntry = (index) => {
    const newEntries = [...medicineEntries];
    newEntries.splice(index, 1);
    setMedicineEntries(newEntries);
    
    // Remove touched state for deleted entry
    const newTouched = [...touchedMedicineEntries];
    newTouched.splice(index, 1);
    setTouchedMedicineEntries(newTouched);
    
    // Remove error state for deleted entry
    const newErrors = [...medicineEntryErrors];
    newErrors.splice(index, 1);
    setMedicineEntryErrors(newErrors);
  };

  // Validate batch number format (medicineName_batchNumber format)
  const validateBatchNumber = (batchNumber, medicineName = '') => {
    // Batch number should be in format medicineName_### or medicineName_batchNumber
    if (!batchNumber || batchNumber.trim() === '') {
      setBatchNumberError("");
      return true; // Allow empty, will be auto-generated
    }
    
    // Clean medicine name for pattern matching
    const cleanMedicineName = medicineName.trim().replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_');
    
    // Check if batch number matches the expected format
    const expectedPattern = new RegExp(`^${cleanMedicineName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_.+$`);
    
    if (!expectedPattern.test(batchNumber)) {
      setBatchNumberError(`Batch Number should be in the format: ${cleanMedicineName}_###`);
      return false;
    }
    
    // Check if batch number already exists (check against current entry's existing batch numbers)
    const currentEntry = medicineEntries.find((entry, idx) => entry.batchNumber === batchNumber);
    if (currentEntry) {
      // Check if it's in the existing batch numbers list
      const entryIndex = medicineEntries.findIndex(entry => entry.batchNumber === batchNumber);
      if (entryIndex >= 0 && medicineEntries[entryIndex].existingBatchNumbers) {
        const exists = medicineEntries[entryIndex].existingBatchNumbers.some(
          item => item.batchNumber === batchNumber
        );
        if (exists) {
          setBatchNumberError("Batch Number already exists");
          return false;
        }
      }
    }
    
    setBatchNumberError("");
    return true;
  };

  // Handle medicine selection in autocomplete
const handleMedicineSelect = (index, newValue) => {
  const newEntries = [...medicineEntries];
  newEntries[index].name = newValue;
  
  const selectedMedicine = medicinesForInvoiceType.find(medicine => medicine.medicineName === newValue);
  if (selectedMedicine) {
    const medicineDetailsType =
      selectedMedicine.medicineDetails || selectedMedicine.medicine_details || "";
    // box/syrup use 1; tablet/capsule/fluid use drops-or-strip count from raw medicine
    if (isBoxOrSyrupMedicine(medicineDetailsType)) {
      newEntries[index].details = 1;
    } else {
      newEntries[index].details = selectedMedicine.details || 1;
    }
    newEntries[index].medicineDetails = medicineDetailsType;
    
    newEntries[index].price = selectedMedicine.price;
    newEntries[index].systemPriceOverride = null; // reset so System Price = unit × qty
    newEntries[index].manufacturer = selectedMedicine.manufacturer;
    newEntries[index].medicineType = selectedMedicine.medicineType;
    
    // CRITICAL: Use the ID from the selected medicine (should be from organization-specific table)
    // The medicineId must exist in the organization-specific medicine_raw_details table
    // for the foreign key constraint to work
    if (selectedMedicine.id) {
      newEntries[index].medicineId = selectedMedicine.id;
      console.log(`[AddMedicineInventory] Selected medicine ID: ${selectedMedicine.id} for medicine: ${newValue}`);
    } else {
      console.error(`[AddMedicineInventory] ⚠️ Selected medicine has no ID:`, selectedMedicine);
      newEntries[index].medicineId = null;
    }
    
    // Fetch batch numbers for selected medicine (only if medicineId is valid)
    if (selectedMedicine.id) {
      // Pass medicine name to fetchExistingBatchNumbers for batch number generation
      fetchExistingBatchNumbers(selectedMedicine.id, index, newValue); // Pass the index and medicine name
    }
  } else {
    // Medicine not found - clear medicineId
    newEntries[index].medicineId = null;
    console.warn(`[AddMedicineInventory] ⚠️ Medicine not found in list: ${newValue}`);
  }
  
  setMedicineEntries(newEntries);
};

  // Validation function to check all medicine entries
  const validateAllMedicineEntries = async () => {
    const errors = [];
    const entryErrors = [];
    
    for (let i = 0; i < medicineEntries.length; i++) {
      const entryErrorsObj = {};
      const entry = medicineEntries[i];
      
      // Explicitly check expiry date
      if (!entry.expiryDate && touchedMedicineEntries[i]?.expiryDate) {
        errors.push({
          index: i,
          field: 'expiryDate',
          message: 'Expiry date is required'
        });
        entryErrorsObj.expiryDate = 'Expiry date is required';
      }
      
      try {
        await medicineEntryValidationSchema.validate(entry, { abortEarly: false });
        // If validation passes, only keep expiry date error if it exists
      } catch (error) {
        if (error.inner && error.inner.length > 0) {
          error.inner.forEach(err => {
            errors.push({
              index: i,
              field: err.path,
              message: err.message
            });
            entryErrorsObj[err.path] = err.message;
          });
        } else if (error.message) {
          // Handle single error without inner array
          errors.push({
            index: i,
            field: error.path || 'expiryDate',
            message: error.message
          });
          entryErrorsObj[error.path || 'expiryDate'] = error.message;
        }
      }
      entryErrors[i] = entryErrorsObj;
    }
    
    // Force state update by creating new array reference
    setMedicineEntryErrors([...entryErrors]);
    return errors;
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAttachment(file);
    formik.setFieldValue("attachment", file.name);
    formik.setFieldError("attachment", undefined);
    formik.setFieldTouched("attachment", true, false);
  };

  const handleDeleteAttachment = () => {
    setAttachment(null);
    formik.setFieldValue("attachment", "");
    formik.setFieldError("attachment", "Attachment is required");
    formik.setFieldTouched("attachment", true, false);
  };

  const handleQuantityChange = (index, value) => {
    const newEntries = [...medicineEntries];
    newEntries[index].quantity = value === "" ? "" : Number(value);
    // Recalculate System Price from unit × qty after qty change
    newEntries[index].systemPriceOverride = null;
    setMedicineEntries(newEntries);
  };

  const handleBatchNumberChange = (index, value) => {
    const newEntries = [...medicineEntries];
    newEntries[index].batchNumber = value;
    setMedicineEntries(newEntries);
    // Validate with medicine name
    const medicineName = newEntries[index].name || '';
    validateBatchNumber(value, medicineName);
  };

  // Calculate total cost from all medicine entries
  const calculateTotalCost = () => {
    const grandTotal = medicineEntries.reduce((sum, entry) => {
      const systemPrice = resolveMedicineSystemLinePrice(entry);
      const discount = parseAmountValue(entry.discount, 0);
      const amountAfterDiscount = systemPrice - discount;
      const taxRate = gstPercentage !== null && gstPercentage > 0 ? gstPercentage : 8.5;
      const tax = (amountAfterDiscount * taxRate) / 100;
      const total = amountAfterDiscount + tax;
      return sum + total;
    }, 0);
    
    // Update formik totalCost field
    formik.setFieldValue('totalCost', grandTotal.toFixed(2));
  };

const calculateInvoiceTax = (amount) => {
  // Use GST percentage from subscription if available, otherwise default to 8.5% (4.25% CGST + 4.25% SGST)
  const taxRate = gstPercentage !== null && gstPercentage > 0 ? gstPercentage : 8.5;
  const tax = (amount * taxRate) / 100; // Total tax
  const sgst = tax / 2; // SGST is half of the total tax (4.25% if default, or half of subscription GST)
  const cgst = tax / 2; // CGST is half of the total tax (4.25% if default, or half of subscription GST)
  const totalAmount = amount + tax; // Total amount including tax

  return {
    amount: amount, // Set the amount
    sgst: Math.round(sgst * 100) / 100, // Set SGST (rounded to 2 decimal places)
    cgst: Math.round(cgst * 100) / 100, // Set CGST (rounded to 2 decimal places)
    totalAmount: Math.round(totalAmount * 100) / 100, // Set total amount (rounded to 2 decimal places)
  };
};

  return (
    <>
    <BasicModal
      open={open}
      onClose={handelCloseModalState}
      title="Add Stock"
      sx={NO_INNER_SCROLL_FORM_MODAL_SX}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX, p: 0 }}
      >
        <Box>
          <form onSubmit={formik.handleSubmit}>

            {/* Section 1 */}
            <StockAddInvoiceSupplierSection
              formik={formik}
              invoiceTypeOptions={[
                { value: "Homeopathy", label: "Homeopathy" },
                { value: "Allopathy", label: "Allopathy" },
              ]}
              suppliers={suppliersList}
              selectedSupplierKey={selectedSupplierKey}
              onSupplierKeyChange={handleSupplierKeyChange}
              isNewSupplier={isNewSupplier}
              errorUpdateTrigger={errorUpdateTrigger}
              onFileChange={handleFileChange}
              onDeleteAttachment={handleDeleteAttachment}
              attachmentFile={attachment}
              showAttachment={true}
            />

            
            <Divider sx={{ mt: 0.5, mb: 0.5 }} />

            {/* Section 2 */}
            <Typography variant="subtitle1" fontWeight={"bold"} pb={0.25}>
              Medicine Details
            </Typography>
                    {/* Medicine Details Section */}
                {medicineEntries.map((entry, index) => (
                <React.Fragment key={index}>
                <Grid container spacing={1} sx={{ mb: 0.5 }}> {/* Add margin-bottom to create space between rows */}
                  <Grid item xs={2.5}>
                    <Autocomplete
                      options={medicinesForInvoiceType.map(med => med.medicineName)}
                      value={entry.name}
                      onChange={(e, newValue) => handleMedicineSelect(index, newValue)}
                      renderOption={(props, option, state) => (
                        <li {...props} key={`${option}-${state.index}`}>
                          {option}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Medicine" variant="outlined" size="small"
                        error={formik.touched.medicineEntries && Boolean(formik.errors.medicineEntries?.[index]?.name)} 
                        helperText={formik.touched.medicineEntries && formik.errors.medicineEntries?.[index]?.name} 
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={1.7}>
                    <TextField
                      fullWidth
                    size="small"
                      label="Batch Number"
                      value={entry.batchNumber}
                      onChange={(e) => handleBatchNumberChange(index, e.target.value)}
                      error={Boolean(batchNumberError)}
                      helperText={batchNumberError}
                      disabled
                    />
                {/* Conditionally render the button based on the specific entry's existingBatchNumbers */}
                  {entry.existingBatchNumbers.length > 0 && (
                  <Typography variant="caption" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    <Link 
                      component="button"
                      variant="caption"
                      onClick={() => {
                        setShowBatchNumbersModal(true);
                        setSelectedEntryIndex(index); // Set the selected entry index
                      }}
                      sx={{
                        color: 'text.secondary'
                      }}
                    >
                      View existing batches ({entry.existingBatchNumbers.length})
                    </Link>
                  </Typography>
                )}
            </Grid>

                <Grid item xs={1.7}>
                  <TextField
                    fullWidth
                    size="small"
                    id={`quantity-${index}`}
                    name="quantity"
                    label="Quantity"
                    type="number"
                    value={entry.quantity}
                    onChange={(e) => handleQuantityChange(index, e.target.value)} // Use the new handler
                    error={formik.touched.medicineEntries && Boolean(formik.errors.medicineEntries?.[index]?.quantity)}
                    helperText={formik.touched.medicineEntries && formik.errors.medicineEntries?.[index]?.quantity}
                  />
                  {/* Display calculated final quantity */}
                  {/* <Typography variant="caption" color="textSecondary">
                    Entered qty ({entry.quantity}) * no. of pieces({entry.details}) = {entry.details === 0 ? entry.quantity : entry.quantity * entry.details}
                  </Typography> */}
                </Grid>
                <Grid item xs={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    id={`price-${index}`}
                    name="price"
                    label="System Price"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    value={
                      entry.systemPriceOverride !== null &&
                      entry.systemPriceOverride !== undefined
                        ? entry.systemPriceOverride
                        : resolveMedicineSystemLinePrice(entry) || ""
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (!isDecimalAmountInput(raw)) return;
                      const newEntries = [...medicineEntries];
                      newEntries[index].systemPriceOverride = raw;
                      setMedicineEntries(newEntries);
                    }}
                    error={
                      Boolean(medicineEntryErrors[index]?.price) ||
                      (formik.touched.medicineEntries &&
                        Boolean(formik.errors.medicineEntries?.[index]?.price))
                    }
                    helperText={
                      medicineEntryErrors[index]?.price ||
                      (formik.touched.medicineEntries &&
                        formik.errors.medicineEntries?.[index]?.price)
                    }
                  />
                  {/* Display calculated total price */}
                  <Typography variant="caption" color="textSecondary">
                    {isFluidMedicine(entry.medicineDetails)
                      ? `cost per bottle: ${entry.price} (${entry.details || 0} drops, 1 ml = ${DROPS_PER_ML} drops)`
                      : usesDetailsMultiplier(entry.medicineDetails)
                        ? `cost per strip: ${entry.price}`
                        : `unit price: ${entry.price}`}
                  </Typography>
                </Grid>

                <Grid item xs={1.2}>
                <TextField
                  fullWidth
                    size="small"
                  id={`vendor-price-${index}`} // Unique ID for the vendor price field
                  name="vendorPrice"
                  label="Supplier Amount"
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  value={entry.vendorPrice} // Bind to the vendorPrice in the entry
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (!isDecimalAmountInput(raw)) return;
                    const newEntries = [...medicineEntries];
                    newEntries[index].vendorPrice = raw;
                    setMedicineEntries(newEntries);
                  }}
                   error={
                     Boolean(medicineEntryErrors[index]?.vendorPrice) ||
                     (formik.touched.medicineEntries &&
                       Boolean(formik.errors.medicineEntries?.[index]?.vendorPrice))
                   }
                   helperText={
                     medicineEntryErrors[index]?.vendorPrice ||
                     (formik.touched.medicineEntries &&
                       formik.errors.medicineEntries?.[index]?.vendorPrice)
                   }
                />
              </Grid>

                <Grid item xs={2}>
                  <FormControl fullWidth error={touchedMedicineEntries[index]?.expiryDate && Boolean(medicineEntryErrors[index]?.expiryDate)}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Expiry Date *"
                        value={entry.expiryDate || null}
                        minDate={dayjs()} // Prevent past dates
                        onChange={(value) => {
                          const newEntries = [...medicineEntries];
                          newEntries[index].expiryDate = value || null;
                          setMedicineEntries(newEntries);
                          
                          // Mark expiry date as touched
                          const newTouched = [...touchedMedicineEntries];
                          if (!newTouched[index]) {
                            newTouched[index] = {};
                          }
                          newTouched[index].expiryDate = true;
                          setTouchedMedicineEntries(newTouched);
                          
                          // Set or clear error based on value
                          const newErrors = [...medicineEntryErrors];
                          if (!newErrors[index]) {
                            newErrors[index] = {};
                          }
                          if (value) {
                            // Clear error if value is set
                            delete newErrors[index].expiryDate;
                          } else {
                            // Set error if empty
                            newErrors[index].expiryDate = "Expiry date is required";
                          }
                          setMedicineEntryErrors(newErrors);
                          setErrorUpdateTrigger(prev => prev + 1);
                          
                          // Validate immediately
                          validateAllMedicineEntries();
                        }}
                        onClose={() => {
                          // Mark expiry date as touched when picker closes
                          const newTouched = [...touchedMedicineEntries];
                          if (!newTouched[index]) {
                            newTouched[index] = {};
                          }
                          newTouched[index].expiryDate = true;
                          setTouchedMedicineEntries(newTouched);
                          
                          // Set error immediately if empty
                          if (!entry.expiryDate) {
                            const newErrors = [...medicineEntryErrors];
                            if (!newErrors[index]) {
                              newErrors[index] = {};
                            }
                            newErrors[index].expiryDate = "Expiry date is required";
                            setMedicineEntryErrors(newErrors);
                            setErrorUpdateTrigger(prev => prev + 1);
                          } else {
                            validateAllMedicineEntries();
                          }
                        }}
                        renderInput={(params) => {
                          const hasError = touchedMedicineEntries[index]?.expiryDate && Boolean(medicineEntryErrors[index]?.expiryDate);
                          return (
                            <TextField
                              {...params}
                              fullWidth
                              size="small"
                              required
                              error={hasError}
                              helperText={hasError ? medicineEntryErrors[index]?.expiryDate : undefined}
                              onBlur={(e) => {
                                params.onBlur?.(e);
                                // Mark as touched and validate
                                const newTouched = [...touchedMedicineEntries];
                                if (!newTouched[index]) {
                                  newTouched[index] = {};
                                }
                                newTouched[index].expiryDate = true;
                                setTouchedMedicineEntries(newTouched);
                                
                                // Set error immediately if empty
                                if (!entry.expiryDate) {
                                  const newErrors = [...medicineEntryErrors];
                                  if (!newErrors[index]) {
                                    newErrors[index] = {};
                                  }
                                  newErrors[index].expiryDate = "Expiry date is required";
                                  setMedicineEntryErrors(newErrors);
                                  setErrorUpdateTrigger(prev => prev + 1);
                                }
                              }}
                            />
                          );
                        }}
                        key={`expiry-date-${index}-${errorUpdateTrigger}`}
                      />
                    </LocalizationProvider>
                    {touchedMedicineEntries[index]?.expiryDate && medicineEntryErrors[index]?.expiryDate && (
                      <FormHelperText>{medicineEntryErrors[index].expiryDate}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={0.5}>
                  <IconButton onClick={() => removeMedicineEntry(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
              </React.Fragment>
            ))}

            {/* Add More Button */}
            <Button
              size="small"
              variant="outlined"
              onClick={addMedicineEntry}
              sx={{ mt: 0.5 }}
            >
              Add More
            </Button>

            {/* Invoice Summary — same layout as Create Invoice */}
            {medicineEntries.length > 0 && (
              <Box sx={{ mt: 0.75, mb: 0.25 }}>
                <Divider sx={{ mb: 1 }} />
                {(() => {
                  const totalSupplierPrice = medicineEntries.reduce((sum, entry) => {
                    const supplierAmount = parseFloat(entry.vendorPrice) || 0;
                    return sum + supplierAmount;
                  }, 0);
                  const discount = parseFloat(formik.values.totalDiscount) || 0;
                  const subtotal = Math.max(0, totalSupplierPrice - discount);
                  const { totalAmount } = calcManualSgstCgstTotal(
                    subtotal,
                    formik.values.sgst,
                    formik.values.cgst
                  );
                  return (
                    <InvoiceSummarySection
                      subtotal={subtotal}
                      manualGst
                      showDiscountInGstRow
                      discountValue={formik.values.totalDiscount}
                      onDiscountChange={(v) =>
                        formik.setFieldValue("totalDiscount", v)
                      }
                      sgstValue={formik.values.sgst}
                      cgstValue={formik.values.cgst}
                      onSgstChange={(v) => formik.setFieldValue("sgst", v)}
                      onCgstChange={(v) => formik.setFieldValue("cgst", v)}
                      totalAmount={totalAmount}
                      paymentModeValue={formik.values.payment_mode}
                      onPaymentModeChange={(value) =>
                        formik.setFieldValue("payment_mode", value)
                      }
                      remarksName="particulars"
                      remarksValue={formik.values.particulars || ""}
                      onRemarksChange={formik.handleChange}
                    />
                  );
                })()}
              </Box>
            )}

            <Grid container spacing={1} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <FormModalActions
                  onCancel={handelCloseModalState}
                  submitType="submit"
                  submitLabel="Create"
                  submitDisabled={formik.isSubmitting}
                  loading={formik.isSubmitting}
                  onSubmit={(e) => {
                    console.log("🔘 Create button clicked");
                    console.log("Formik isSubmitting:", formik.isSubmitting);
                    console.log("Formik errors:", formik.errors);
                    console.log("Formik values:", formik.values);
                    console.log("Medicine entries:", medicineEntries);
                    // Let formik handle the submission
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </Box>
    </BasicModal>

  {/* Batch Numbers Modal */}
    <BasicModal
      open={showBatchNumbersModal}
      onClose={() => setShowBatchNumbersModal(false)}
      sx={COMPACT_FORM_MODAL_SX}
    >
      <Typography variant="h6" gutterBottom>
        Existing Batch Numbers
      </Typography>
      <List sx={{ maxHeight: '300px', overflowY: 'auto', padding: 0 }}>
        {selectedEntryIndex !== null && medicineEntries[selectedEntryIndex] && medicineEntries[selectedEntryIndex].existingBatchNumbers ? (
          medicineEntries[selectedEntryIndex].existingBatchNumbers.length > 0 ? (
            medicineEntries[selectedEntryIndex].existingBatchNumbers.map((item, i) => (
              <ListItem key={i} sx={{ padding: '4px 16px' }}>
                <ListItemText primary={`${item.batchNumber} - qty: ${item.quantity}`} />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText primary="No existing batch numbers available." />
            </ListItem>
          )
        ) : (
          <ListItem>
            <ListItemText primary="No existing batch numbers available." />
          </ListItem>
        )}
      </List>
    </BasicModal>

    </>

  );
};

export default AddMedicineInventory;
